/**
 * User API Service
 */
import { get, post, put, del } from './api'

/**
 * Register a new user
 * @param {Object} userData - { username, email, password, age?, gender?, country? }
 * @returns {Promise<Object>} Created user response { id, username, email, age, gender, country }
 */
export function registerUser(userData) {
    return post('/users/register', userData)
}

/**
 * Login user
 * @param {Object} credentials - { email, password }
 * @returns {Promise<Object>} Token response { token }
 */
export function loginUser(credentials) {
    return post('/users/login', credentials)
}

/**
 * Get user by ID
 * @param {string} id - User ID
 * @returns {Promise<Object>} User data { id, username, email, age, gender, country }
 */
export function getUser(id) {
    return get(`/users/${id}`)
}

/**
 * Get current logged-in user details (Me)
 * @param {Object} [options={}] - Request options
 * @returns {Promise<Object>} User data { id, username, email, role, ... }
 */
export function getCurrentUser(options = {}) {
    return get('/users/me', { requiresAuth: true, ...options })
}

/**
 * Update user
 * @param {string} id - User ID
 * @param {Object} userData - { username?, email?, password?, age?, gender?, country? }
 * @returns {Promise<Object>} Update response
 */
export function updateUser(id, userData) {
    return put(`/users/${id}`, userData, { requiresAuth: true }) // Requires auth
}

/**
 * Delete user
 * @param {string} id - User ID
 * @returns {Promise<Object>} Delete response
 */
export function deleteUser(id) {
    return del(`/users/${id}`, null, { requiresAuth: true }) // Requires auth
}

/**
 * Get user activity
 * @param {Object} [params={}] - { limit, offset }
 * @param {Object} [options={}] - Request options
 * @returns {Promise<Object>} Activity data
 */
export function getUserActivity(params = {}, options = {}) {
    return get('/users/me/activity', { 
        params, 
        requiresAuth: true, 
        ...options 
    })
}

/**
 * Request password reset
 * @param {string} email
 * @returns {Promise<Object>} Response message
 */
export function requestReset(email) {
    return post('/auth/forgot-password', { email })
}

/**
 * Reset password
 * @param {string} token
 * @param {string} newPassword
 * @returns {Promise<Object>} Response message
 */
export function resetPassword(token, newPassword) {
    return post('/auth/reset-password', { token, new_password: newPassword })
}

/**
 * Verify email with token
 * @param {string} token
 * @returns {Promise<Object>} Success message
 */
export function verifyEmail(token) {
    return get('/auth/verify-email', { params: { token } })
}

export default { registerUser, loginUser, getUser, getCurrentUser, updateUser, deleteUser, getUserActivity, requestReset, resetPassword, verifyEmail }
